import L from 'leaflet';
import { useEffect } from 'react';
import { useMap } from 'react-leaflet';

/** 매출 심볼 하나 — 좌표는 전국 스냅샷에서, 금액은 GET /me/stores/sales 에서 온다 */
export interface StoreSalesPoint {
  storeName: string;
  latitude: number;
  longitude: number;
  amount: number;
}

/**
 * 매출 색 램프 — 붉을수록 매출이 크다. 단일 색상(빨강) 명도 순차 램프(밝음→어두움).
 * 경계값을 데이터가 아니라 금액으로 고정해 월을 바꿔도 같은 색이 같은 금액대를 뜻한다
 * (반지름의 k 를 고정한 것과 같은 이유).
 * ponytail: 경계값 고정. 매출 규모가 크게 다른 회사가 들어오면 회사별 경계가 필요하다.
 */
const SALES_RAMP = ['#fca5a5', '#f87171', '#ef4444', '#dc2626', '#991b1b'];
const SALES_THRESHOLDS = [100_000_000, 300_000_000, 500_000_000, 800_000_000];

/** 밝은 채움 위에서도 원 경계가 보이도록 테두리는 램프 최고 단계로 고정한다 */
export const SALES_SYMBOL_STROKE = SALES_RAMP[SALES_RAMP.length - 1];

/** 금액 → 채움 색 */
export function salesColor(amount: number): string {
  const idx = SALES_THRESHOLDS.findIndex(t => amount <= t);
  return idx === -1 ? SALES_RAMP[SALES_RAMP.length - 1] : SALES_RAMP[idx];
}

/**
 * 반지름 기준점 — 5억원을 26px 로 두고 r = k·√금액 로 키운다.
 * 면적이 금액에 비례하므로 같은 크기는 언제나 같은 금액이다(월을 바꿔도 불변).
 * ponytail: k 고정. 매출 규모가 크게 다른 회사가 들어오면 회사별 기준액이 필요하다.
 */
const RADIUS_K = 26 / Math.sqrt(500_000_000);

/** 금액 → 반지름(px). 면적 비례 인코딩이라 최소 반지름 클램프를 두지 않는다. */
export function salesRadius(amount: number): number {
  return RADIUS_K * Math.sqrt(Math.max(amount, 0));
}

/** 매장명은 관리자 입력값이라 innerHTML 로 넣지 않는다 (Leaflet 은 문자열을 innerHTML 로 쓴다) */
function tooltipContent(point: StoreSalesPoint): HTMLElement {
  const el = document.createElement('div');
  const name = document.createElement('span');
  name.className = 'font-semibold';
  name.textContent = point.storeName;
  el.append(name, document.createElement('br'), `${point.amount.toLocaleString()}원`);
  return el;
}

interface StoreSalesLayerProps {
  active: boolean;
  points: StoreSalesPoint[];
}

/**
 * 매출 비례 심볼 레이어.
 *
 * 마커 클러스터와 같은 그룹에 넣으면 줌 아웃 때 클러스터에 흡수돼 심볼이 사라지므로
 * 별도 LayerGroup 으로 관리한다. 벡터 레이어라 기본 overlayPane(z400)에 그려져
 * 마커(markerPane, z600) 아래에 깔린다 — 기존 마커 클릭을 가리지 않는다.
 */
export function StoreSalesLayer({ active, points }: StoreSalesLayerProps) {
  const map = useMap();

  useEffect(() => {
    if (!active || points.length === 0) return;

    const group = L.layerGroup(
      points.map(p =>
        L.circleMarker([p.latitude, p.longitude], {
          radius: salesRadius(p.amount),
          color: SALES_SYMBOL_STROKE,
          weight: 2,
          fillColor: salesColor(p.amount),
          // 색 단계를 읽을 만큼 진하게. 겹침은 테두리로 구분된다
          fillOpacity: 0.55,
        }).bindTooltip(tooltipContent(p), { direction: 'top', sticky: true })
      )
    );
    group.addTo(map);

    return () => { map.removeLayer(group); };
  }, [active, points, map]);

  return null;
}
